// EWM-Algolia v1 — public exports + helpers.
//
// See `docs/superpowers/plans/2026-05-11-algolia-plp-integration.md` for
// the full architectural plan. v1 ships read-only client-side queries; v2
// adds SSR + ISR + Insights.

export { getAlgoliaSearchClient } from './client';
export {
  initInsights,
  setAuthenticatedUser,
  getUserToken,
  trackClick,
  trackClickedFilters,
  trackView,
  trackConvert,
} from './insights';
export type {
  AlgoliaProduct,
  AlgoliaPriceGroupEntry,
  AlgoliaActivePrice,
  AlgoliaCurrencyAmount,
  AlgoliaProductImage,
  AlgoliaFacetCounts,
} from './types';

import { siteCodeFromLocale } from '@shared/types/product/regionAvailability';
import type { Locale } from '@/project.config';

// Locale → market suffix used in `{env}_products_{market}`. Delegates to the
// shared region engine so the Algolia index selection and the BFF product
// region gate are driven by ONE rule (siteCodeFromLocale) and can't drift:
// en-NZ → sdnz, everything else → sdau.
const localeToMarket = siteCodeFromLocale;

// Env prefix used in the index name. We treat NODE_ENV=production as the
// `prod` tier; everything else (dev / preview / Storybook) reads from
// `dev`. `uat` is not auto-selected — surfaces opt in by passing the
// env override explicitly when needed.
const currentEnvPrefix = (): 'dev' | 'uat' | 'prod' => {
  const override = process.env.NEXT_PUBLIC_ALGOLIA_ENV;
  if (override === 'dev' || override === 'uat' || override === 'prod') return override;
  return process.env.NODE_ENV === 'production' ? 'prod' : 'dev';
};

/**
 * Compute the Algolia index name for the dealer's locale.
 *
 * @example
 *   getProductsIndexName('en-AU') // → 'dev_products_sdau' (or 'prod_products_sdau' in prod)
 */
export const getProductsIndexName = (
  locale: Locale,
  // EWM-255 — `createdat_desc` is the Newest-sort virtual replica.
  // The replica must exist for every {env}_products_sd{market} primary
  // currently in service (today: dev_sdau, dev_sdnz). Add the matching
  // `_createdat_desc` replica via Algolia's Sort By Attribute UI when
  // standing up a new env/market primary (e.g. when prod_sdau is first
  // deployed), or the FE call 404s on that market when the user picks
  // Newest.
  sort?:
    'name_asc' | 'name_desc' | 'price_asc' | 'price_desc' | 'save_desc' | 'sku_asc' | 'sku_desc' | 'createdat_desc',
): string => {
  const base = `${currentEnvPrefix()}_products_${localeToMarket(locale)}`;
  return sort ? `${base}_${sort}` : base;
};
